import type { MouseEvent } from 'react'
import { AlertCircle, Loader2, Pause, Play } from 'lucide-react'
import { usePlayerActions, usePlayerState, type PlayerTrack } from '@/components/player/PlayerProvider'

interface PlayButtonProps {
  track: PlayerTrack
  // solid: a standalone round button. overlay: fills its (relative, rounded)
  // parent -- an album cover that becomes a play button.
  variant?: 'solid' | 'overlay'
  className?: string
  // Play this track AS PART OF a list: when it ends the next one starts.
  queue?: { tracks: PlayerTrack[]; index: number }
}

export function PlayButton({ track, variant = 'solid', className = '', queue }: PlayButtonProps) {
  const current = usePlayerState()
  const { play, playQueue } = usePlayerActions()
  const status = current.track?.key === track.key ? current.status : 'idle'
  const active = status !== 'idle'

  const Icon = status === 'loading' ? Loader2 : status === 'playing' ? Pause : status === 'unavailable' ? AlertCircle : Play
  const label =
    status === 'playing'
      ? `Pause ${track.title}`
      : status === 'unavailable'
        ? `No preview available for ${track.title}`
        : `Play a preview of ${track.title}`

  const handleClick = (event: MouseEvent) => {
    // These sit inside links and rows -- a play click shouldn't navigate.
    event.preventDefault()
    event.stopPropagation()
    if (queue) playQueue(queue.tracks, queue.index)
    else play(track)
  }

  const base = 'flex items-center justify-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
  const styles =
    variant === 'overlay'
      ? `absolute inset-0 rounded-[inherit] bg-black/50 text-white ${
          active ? 'opacity-100' : 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100'
        }`
      : 'size-10 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:scale-105'

  return (
    <button type="button" onClick={handleClick} aria-label={label} title={label} className={`${base} ${styles} ${className}`}>
      <Icon className={`${variant === 'overlay' ? 'size-5' : 'size-4'} ${status === 'loading' ? 'animate-spin' : ''} ${status === 'playing' || status === 'idle' || status === 'paused' ? 'fill-current' : ''}`} />
    </button>
  )
}
